import type React from 'react';
import { Space, Form, Input } from 'antd';
import { LazyLoadingSelect } from 'components/ui/lazy-loading-select';
import { SortedSelect } from 'components/ui/sorted-select';
import {
  insuredApi,
  brokerApi,
  reinsuredApi,
  binderApi,
  userApi,
} from 'services/apis';
import {
  InsuredResponseDtoStatusEnum,
  BinderClaimResponseDtoClaimStatusEnum,
} from 'libs/pinnacle-sdk';
import type { TFunction } from 'i18next';
import { uniqBy } from 'lodash';

interface BinderClaimFiltersProps {
  t: TFunction;
  businessLines: any;
  isLoadingBusinessLines: boolean;
}

const BinderClaimFilters: React.FC<BinderClaimFiltersProps> = ({
  t,
  businessLines,
  isLoadingBusinessLines,
}) => {
  return (
    <Space direction="horizontal" wrap>
      <Form.Item name="binderClaimNo" className="min-w-[200px] my-2">
        <Input
          allowClear
          size="large"
          placeholder={t('binderClaims.claimNo')}
        />
      </Form.Item>

      <Form.Item name="binderInsuredIds" className="min-w-[220px] my-2">
        <LazyLoadingSelect
          mode="multiple"
          placeholder={t('insured.selectInsured')}
          fetchOptions={async (page, pageSize, searchText) => {
            const { items } = await insuredApi.filterInsuredsClaims({
              page,
              pageSize,
              insuredFilterQueryDto: {
                searchText,
                statuses: [InsuredResponseDtoStatusEnum.Active],
              },
            });
            return {
              data: items.map((insured) => ({
                label: insured.name,
                value: insured.insuredId,
              })),
            };
          }}
          maxTagCount={1}
          size="large"
          allowClear
          queryKey={'binderClaimsInsuredLazyLoadingSelect'}
        />
      </Form.Item>

      <Form.Item name="binderBrokerNames" className="min-w-[220px] my-2">
        <LazyLoadingSelect
          mode="multiple"
          placeholder={t('binderClaims.selectBroker')}
          fetchOptions={async (page, pageSize, searchText) => {
            const { items } = await brokerApi.filterBrokers({
              page,
              pageSize,
              searchText,
            });
            return {
              data: items.map((broker: any) => ({
                label: broker.name,
                value: broker.name,
              })),
            };
          }}
          maxTagCount={1}
          size="large"
          allowClear
          queryKey={'binderClaimsBrokerLazyLoadingSelect'}
        />
      </Form.Item>

      <Form.Item name="binderReinsuredNames" className="min-w-[220px] my-2">
        <LazyLoadingSelect
          mode="multiple"
          placeholder={t('binderClaims.selectReinsured')}
          fetchOptions={async (page, pageSize, searchText) => {
            const { items } = await reinsuredApi.filterReinsureds({
              page,
              pageSize,
              searchText,
            } as any);
            return {
              data: items.map((reinsured: any) => ({
                label: reinsured.name,
                value: reinsured.name,
              })),
            };
          }}
          maxTagCount={1}
          size="large"
          allowClear
          queryKey={''}
        />
      </Form.Item>

      <Form.Item name="binderNames" className="min-w-[220px] my-2">
        <LazyLoadingSelect
          mode="multiple"
          placeholder={t('binderClaims.selectBinder')}
          fetchOptions={async (page, pageSize, searchText) => {
            const { items } = await binderApi.filterBinders({
              binderFilterQueryDto: { searchText },
              page,
              pageSize,
            } as any);
            return {
              data: items.map((binder: any) => ({
                label: binder.binderName,
                value: binder.binderName,
              })),
            };
          }}
          maxTagCount={1}
          size="large"
          allowClear
          queryKey={''}
        />
      </Form.Item>

      <Form.Item name="binderLineOfBusiness" className="min-w-[220px] my-2">
        <SortedSelect
          mode="multiple"
          allowClear
          size="large"
          loading={isLoadingBusinessLines}
          placeholder={t('submission.selectLineOfBusiness')}
          options={uniqBy(businessLines || [], 'name').map(
            (businessLine: any) => ({
              label: businessLine.name,
              value: businessLine.name,
            })
          )}
        />
      </Form.Item>

      <Form.Item name="binderClaimsStatus" className="min-w-[220px] my-2">
        <SortedSelect
          mode="multiple"
          placeholder={t('masterClaims.selectClaimsStatus')}
          options={Object.values(BinderClaimResponseDtoClaimStatusEnum).map(
            (name: any) => ({
              label: name,
              value: name,
            })
          )}
          allowClear
          maxTagCount={1}
          showSearch
          size="large"
          className="rounded-xl"
        />
      </Form.Item>

      <Form.Item name="binderClaimsHandlerIds" className="min-w-[220px] my-2">
        <LazyLoadingSelect
          mode="multiple"
          placeholder={t('masterClaims.selectClaimsHandler')}
          fetchOptions={async (page, pageSize, searchText) => {
            const { items } = await userApi.filterUsers({
              searchText,
              page,
              pageSize,
            });
            return {
              data: items.map((user) => ({
                label: user.name || '',
                value: user.userId,
              })),
            };
          }}
          allowClear
          maxTagCount={1}
          showSearch
          size="large"
          className="rounded-xl"
          queryKey="binder-claimsHandler-lazy-loading-select"
        />
      </Form.Item>
    </Space>
  );
};

export default BinderClaimFilters;
